import * as R from 'ramda'
import i18n from '@/locales'
import { PERMISSION as POLICY_MAP } from '@/constants/permission'
import { RESOURCES_MAP, SERVICES_MAP } from './constants'

// 异步渲染组织节点标签
const OrgNodeTags = {
  name: 'OrgNodeTags',
  props: {
    ids: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      list: [],
      loading: false,
    }
  },
  async created () {
    if (!this.ids || !this.ids.length) return
    this.loading = true
    try {
      const manager = new this.$Manager('organization_nodes')
      const res = await manager.list({
        params: {
          scope: this.$store.getters.scope,
          filter: `id.in(${this.ids.join(',')})`,
          limit: this.ids.length,
        },
      })
      this.list = res.data?.data || []
    } catch (error) {
      this.list = []
    } finally {
      this.loading = false
    }
  },
  render (h) {
    if (this.loading) return h('span', this.$t('common.loading'))
    if (!this.list.length) return h('span', '-')
    const tags = this.list.map(item => h('a-tag', { class: 'mr-1 mb-1' }, item.name || item.id))
    return h('div', tags)
  },
}

/**
 * 翻译权限展示名
 * 1. 优先查找 SERVICES_MAP RESOURCES_MAP 指定的名
 * 2. 查找 i18n 中 `${service}.${resourceKey}` 的名称
 * 3. 查找 i18n 中 `dictionary.${resourceKey}` 的名称
 * 4. 都找不到时，返回 resourceKey
 * 建议：ce 中涉及的，可在 dictionary 和 SERVICES_MAP、RESOURCES_MAP 添加，ee 和 oem 涉及的，添加至 该模块`${service}.${resourceKey}` 即可
 */
export function transformLabel (config, resourceKey, service) {
  if (service === 'identity' && resourceKey === 'services') {
    return i18n.t('compute.text_502')
  }
  if (config) {
    if (config.i18n) return i18n.t(config.i18n)
    return config.label
  } else if (service && i18n.te(`${service}.${resourceKey}`)) {
    return i18n.t(`${service}.${resourceKey}`)
  }
  return i18n.te(`dictionary.${resourceKey}`) ? i18n.t(`dictionary.${resourceKey}`) : resourceKey
}

/**
 * 根据定义的 POLICY_MAP 生成 POLICY_GROUPS 供设置权限表单使用
 *
 * @returns { Array }
 */
export function genPolicyGroups () {
  const temp = {}
  // 存放找到的resources，保证唯一性
  const resources = []
  for (const key in POLICY_MAP) {
    const item = POLICY_MAP[key]
    // 将 POLICY_MAP 每项的 array value 第一项拿出来作为 key
    // 如果没有声明初始化进行
    if (!temp[item[0]]) {
      temp[item[0]] = {
        label: transformLabel(SERVICES_MAP[item[0]], item[0]),
        service: item[0],
        resources: [],
      }
    }
    // 由验证<resource>为唯一项，改为验证<service>_<resource>
    if (!resources.includes(`${item[0]}_${item[1]}`)) {
      resources.push(`${item[0]}_${item[1]}`)
      const res = {
        label: transformLabel(RESOURCES_MAP[item[1]], item[1], item[0]),
        resource: item[1],
      }
      if (RESOURCES_MAP[item[1]] && RESOURCES_MAP[item[1]].extras) {
        res.extras = RESOURCES_MAP[item[1]].extras
      }
      temp[item[0]].resources.push(res)
    }
  }
  const ret = Object.values(temp).map(item => {
    return {
      ...item,
      resources: [...item.resources, { label: i18n.t('iam.others'), resource: '*' }],
    }
  })
  return ret
}

/**
 * 对比策略差异，返回新增、删除、变更的 action 资源组合
 * @param {Object|String} originPolicy
 * @param {Object|String} newPolicy
 * @returns {{ added: Array, removed: Array, changed: Array, isEqual: Boolean }}
 */
export function diffPolicies (originPolicy, newPolicy) {
  const normalize = (policy) => {
    if (!policy) return {}
    let policyObj = policy
    if (typeof policy === 'string') {
      try {
        policyObj = JSON.parse(policy)
      } catch (error) {
        return {}
      }
    }
    const ret = {}
    // statement 格式
    const statements = policyObj?.statement
    if (Array.isArray(statements)) {
      statements.forEach(stmt => {
        const actions = Array.isArray(stmt.action) ? stmt.action : [stmt.action]
        const resources = Array.isArray(stmt.resource) ? stmt.resource : [stmt.resource]
        actions.forEach(action => {
          resources.forEach(resource => {
            const key = `${resource}::${action}`
            ret[key] = stmt.effect || 'allow'
          })
        })
      })
      return ret
    }
    // map 结构格式 service -> resource -> action -> extra
    const mapPolicy = policyObj || {}
    Object.keys(mapPolicy).forEach(service => {
      const serviceVal = mapPolicy[service]
      if (!serviceVal || typeof serviceVal !== 'object') return
      Object.keys(serviceVal).forEach(resource => {
        const resourceVal = serviceVal[resource]
        if (!resourceVal || typeof resourceVal !== 'object') return
        Object.keys(resourceVal).forEach(action => {
          const actionVal = resourceVal[action]
          if (!actionVal || typeof actionVal !== 'object') return
          Object.keys(actionVal).forEach(extra => {
            const effect = actionVal[extra]
            const key = `${service}.${resource}.${action}.${extra}`
            ret[key] = effect
          })
        })
      })
    })
    return ret
  }

  const origin = normalize(originPolicy)
  const latest = normalize(newPolicy)
  const added = []
  const removed = []
  const changed = []

  const buildOptionsFromKeys = (keys = []) => {
    const options = {}
    const parseKey = (key) => {
      if (key.includes('::')) {
        const [resourcePath, action] = key.split('::')
        const [service, resource] = resourcePath.split(':')
        return { service, resource, action }
      }
      const [service, resource, action, perform] = key.split('.')
      return { service, resource, action, perform }
    }
    keys.forEach(key => {
      const { service, resource, action, perform } = parseKey(key)
      if (!service || !resource || !action) return

      const serviceOption = SERVICES_MAP[service] || {}
      const resourceOption = RESOURCES_MAP[resource] || {}
      const performOpt = perform && resourceOption.extras?.length ? R.find(R.propEq('value', perform))(resourceOption.extras) : null
      const actionRet = {
        label: performOpt ? performOpt.label : i18n.t(`policyDefaultActions.${action}`),
        key: performOpt ? performOpt.value : action,
      }
      if (!options[service]) {
        options[service] = {
          label: serviceOption.i18n ? i18n.t(serviceOption.i18n) : serviceOption.label || service,
          key: service,
          children: {
            [resource]: {
              label: resourceOption.i18n ? i18n.t(resourceOption.i18n) : resourceOption.label || resource,
              key: resource,
              actions: [actionRet],
            },
          },
        }
      } else {
        if (!options[service].children[resource]) {
          options[service].children[resource] = {
            label: resourceOption.i18n ? i18n.t(resourceOption.i18n) : resourceOption.label || resource,
            key: resource,
            actions: [actionRet],
          }
        } else {
          const actionOpt = R.find(R.propEq('key', actionRet.key))(options[service].children[resource].actions)
          if (!actionOpt) {
            options[service].children[resource].actions.push(actionRet)
          }
        }
      }
    })
    return options
  }

  Object.keys(latest).forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(origin, key)) {
      added.push(key)
    } else if (origin[key] !== latest[key]) {
      // effect 变化：deny -> allow 视为新增；allow -> deny 视为删除；其他变化视为变更
      const oldEffect = origin[key]
      const newEffect = latest[key]
      if (oldEffect === 'deny' && newEffect === 'allow') {
        added.push(key)
      } else if (oldEffect === 'allow' && newEffect === 'deny') {
        removed.push(key)
      } else {
        changed.push(key)
      }
    }
  })

  Object.keys(origin).forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(latest, key)) {
      removed.push(key)
    }
  })

  return {
    added,
    removed,
    changed,
    addedOptions: buildOptionsFromKeys(added),
    removedOptions: buildOptionsFromKeys(removed),
    changedOptions: buildOptionsFromKeys(changed),
    isEqual: added.length === 0 && removed.length === 0 && changed.length === 0,
  }
}

// 通用工单展示配置列
export const getShopCartWorkflowResourceConfigColumn = () => {
  return {
    field: 'config',
    title: i18n.t('common_428'),
    slots: {
      default: ({ row }, h) => {
        const { action, parameter = {} } = row
        const ret = []
        if (action === 'update') {
          const renderTags = (tags = []) => {
            return tags.map(item => {
              const label = `${(item.key || '').replace('user:', '')}:${item.value || ''}`
              return <a-tag class="mr-1 mb-1">{label}</a-tag>
            })
          }
          if (parameter.type) ret.push(<div>{i18n.t('common.name')}: {parameter.type}</div>)
          if (parameter.description) ret.push(<div>{i18n.t('common.description')}: {parameter.description}</div>)
          if (parameter.domain_tags && parameter.domain_tags.length) {
            ret.push(<div>{i18n.t('iam.domain_tag')}: {...renderTags(parameter.domain_tags)}</div>)
          }
          if (parameter.project_tags && parameter.project_tags.length) {
            ret.push(<div>{i18n.t('iam.project_tag')}: {...renderTags(parameter.project_tags)}</div>)
          }
          if (parameter.object_tags && parameter.object_tags.length) {
            ret.push(<div>{i18n.t('iam.object_tag')}: {...renderTags(parameter.object_tags)}</div>)
          }
          if (parameter.org_node_id && parameter.org_node_id.length) {
            ret.push(<div>{i18n.t('dictionary.organization')}: {h(OrgNodeTags, { props: { ids: parameter.org_node_id } })}</div>)
          }
        }
        return ret.length ? ret : '-'
      },
    },
  }
}
